import * as Sentry from '@sentry/nextjs'
import axios from 'axios'

// Record handling provenance, not a status/message class. Weak references avoid
// retaining request configs (including credentials) after the error is gone.
const handledSignOutErrors = new WeakSet<object>()

// Call only after the existing signOut promise has fulfilled. Unknown/relative
// request destinations fail closed; they must not acquire suppression provenance.
export function recordHandledSignOut(error: unknown) {
  if (!axios.isAxiosError(error) || error.response?.status !== 401) return
  if (!error.config?.url) return

  const configuredApis = [['current', process.env.NEXT_PUBLIC_API_URL]] as const

  try {
    const requestUrl = new URL(
      axios.getUri({
        url: error.config.url,
        // Use only this failed request's config, never unrelated axios defaults.
        baseURL: error.config.baseURL ?? '',
        allowAbsoluteUrls: error.config.allowAbsoluteUrls ?? true,
        params: null
      })
    )
    for (const [api, baseUrl] of configuredApis) {
      if (!baseUrl) continue
      const apiUrl = new URL(baseUrl)
      if (
        (apiUrl.protocol === 'https:' || apiUrl.protocol === 'http:') &&
        requestUrl.origin === apiUrl.origin
      ) {
        handledSignOutErrors.add(error)
        // Keep the operational transition visible without serializing the error,
        // headers, tokens or request URL/query.
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'info',
          message: 'API unauthorized response handled by sign-out',
          data: { status: 401, action: 'sign_out', api }
        })
        return
      }
    }
  } catch {
    // Invalid or unknown URL configuration remains reportable.
  }
}

export function isHandledSignOut(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    handledSignOutErrors.has(error)
  )
}
